import React from 'react';
import { useMobile, useResponsiveValue } from '../../hooks/useMobile';

/**
 * ResponsiveGrid - Adaptive grid layout
 * Automatically adjusts columns based on screen size
 */
export const ResponsiveGrid = ({ 
  children, 
  columns = { xs: 1, sm: 2, md: 3, lg: 4 },
  gap = 4,
  className = ''
}) => {
  const responsiveColumns = useResponsiveValue(columns);
  
  const gridClasses = {
    1: 'grid-cols-1',
    2: 'grid-cols-2', 
    3: 'grid-cols-3',
    4: 'grid-cols-4',
    5: 'grid-cols-5',
    6: 'grid-cols-6'
  };

  const gapClasses = {
    1: 'gap-1',
    2: 'gap-2',
    3: 'gap-3',
    4: 'gap-4',
    5: 'gap-5',
    6: 'gap-6',
    8: 'gap-8'
  };

  return (
    <div className={`grid ${gridClasses[responsiveColumns]} ${gapClasses[gap]} ${className}`}>
      {children}
    </div>
  );
};

/**
 * ResponsiveStack - Vertical stack with responsive spacing
 */
export const ResponsiveStack = ({ 
  children, 
  spacing = { xs: 4, lg: 6 },
  className = ''
}) => {
  const responsiveSpacing = useResponsiveValue(spacing);
  
  const spaceClasses = {
    1: 'space-y-1',
    2: 'space-y-2',
    3: 'space-y-3', 
    4: 'space-y-4',
    5: 'space-y-5',
    6: 'space-y-6',
    8: 'space-y-8'
  };

  return (
    <div className={`${spaceClasses[responsiveSpacing]} ${className}`}>
      {children}
    </div>
  );
};

/**
 * ResponsiveContainer - Max width container with responsive padding
 */
export const ResponsiveContainer = ({ 
  children, 
  maxWidth = '7xl',
  padding = { xs: 4, lg: 8 },
  className = ''
}) => {
  const responsivePadding = useResponsiveValue(padding);
  
  const maxWidthClasses = {
    'sm': 'max-w-sm',
    'md': 'max-w-md',
    'lg': 'max-w-lg',
    'xl': 'max-w-xl',
    '2xl': 'max-w-2xl',
    '3xl': 'max-w-3xl',
    '4xl': 'max-w-4xl',
    '5xl': 'max-w-5xl',
    '6xl': 'max-w-6xl',
    '7xl': 'max-w-7xl',
    'full': 'max-w-full'
  };

  const paddingClasses = {
    0: 'px-0',
    1: 'px-1',
    2: 'px-2',
    3: 'px-3',
    4: 'px-4',
    5: 'px-5',
    6: 'px-6',
    8: 'px-8'
  };

  return (
    <div className={`${maxWidthClasses[maxWidth]} mx-auto ${paddingClasses[responsivePadding]} ${className}`}>
      {children}
    </div>
  );
};

/**
 * ResponsiveList - List component that switches between table and cards
 */
export const ResponsiveList = ({ 
  items = [],
  renderCard,
  renderTableRow,
  tableHeaders = [],
  loading = false,
  empty,
  className = ''
}) => {
  const { isMobile } = useMobile();

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-gray-600">Loading...</span>
      </div>
    );
  }

  if (items.length === 0) {
    return empty || (
      <div className="text-center py-8 text-gray-500">
        <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2M4 13h2m0 0V9a2 2 0 012-2h2a2 2 0 012 2v4.01" />
        </svg>
        <p className="mt-2">No items found</p>
      </div>
    );
  }

  // Mobile: render as cards
  if (isMobile) {
    return (
      <ResponsiveStack spacing={{ xs: 3 }} className={className}>
        {items.map((item, index) => renderCard(item, index))}
      </ResponsiveStack>
    );
  }

  // Desktop: render as table
  return (
    <div className={`overflow-x-auto ${className}`}>
      <table className="min-w-full divide-y divide-gray-200">
        {tableHeaders.length > 0 && (
          <thead className="bg-gray-50">
            <tr>
              {tableHeaders.map((header, index) => (
                <th 
                  key={index}
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>
        )}
        <tbody className="bg-white divide-y divide-gray-200">
          {items.map((item, index) => renderTableRow(item, index))}
        </tbody>
      </table>
    </div>
  );
};

/**
 * ResponsiveText - Text with responsive sizing
 */
export const ResponsiveText = ({ 
  children, 
  size = { xs: 'sm', lg: 'base' },
  weight = 'normal',
  color = 'gray-900',
  className = ''
}) => {
  const responsiveSize = useResponsiveValue(size);
  
  const sizeClasses = {
    'xs': 'text-xs',
    'sm': 'text-sm', 
    'base': 'text-base',
    'lg': 'text-lg',
    'xl': 'text-xl',
    '2xl': 'text-2xl',
    '3xl': 'text-3xl'
  };

  const weightClasses = {
    'light': 'font-light',
    'normal': 'font-normal',
    'medium': 'font-medium',
    'semibold': 'font-semibold',
    'bold': 'font-bold'
  };

  return (
    <span className={`${sizeClasses[responsiveSize]} ${weightClasses[weight]} text-${color} ${className}`}>
      {children}
    </span>
  );
};

/**
 * ResponsiveButton - Button with responsive sizing and touch targets
 */
export const ResponsiveButton = ({ 
  children,
  size = { xs: 'md', lg: 'md' },
  variant = 'primary',
  fullWidth = false,
  onClick,
  disabled = false,
  className = ''
}) => {
  const { isMobile, isTouchDevice } = useMobile();
  const responsiveSize = useResponsiveValue(size);
  
  const sizeClasses = {
    'sm': `px-3 py-2 text-sm ${isTouchDevice ? 'min-h-[44px]' : ''}`,
    'md': `px-4 py-2 text-sm ${isTouchDevice ? 'min-h-[44px]' : ''}`,
    'lg': `px-6 py-3 text-base ${isTouchDevice ? 'min-h-[48px]' : ''}`
  };

  const variantClasses = {
    'primary': 'bg-blue-600 text-white hover:bg-blue-700 disabled:bg-blue-300',
    'secondary': 'bg-gray-200 text-gray-900 hover:bg-gray-300 disabled:bg-gray-100',
    'outline': 'border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:bg-gray-50',
    'danger': 'bg-red-600 text-white hover:bg-red-700 disabled:bg-red-300'
  };

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`
        ${sizeClasses[responsiveSize]}
        ${variantClasses[variant]}
        ${fullWidth ? 'w-full' : ''}
        font-medium rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500
        disabled:cursor-not-allowed disabled:opacity-50
        ${isTouchDevice ? 'active:scale-95' : ''}
        ${className}
      `}
    >
      {children}
    </button>
  );
};

/**
 * ResponsiveCard - Basic card with responsive padding
 */
export const ResponsiveCard = ({ 
  children, 
  padding = { xs: 4, lg: 6 },
  className = ''
}) => {
  const responsivePadding = useResponsiveValue(padding);
  
  const paddingClasses = {
    2: 'p-2',
    3: 'p-3',
    4: 'p-4',
    5: 'p-5',
    6: 'p-6',
    8: 'p-8'
  };

  return (
    <div className={`bg-white rounded-lg border border-gray-200 ${paddingClasses[responsivePadding]} ${className}`}>
      {children}
    </div>
  );
};

/**
 * ShowOnMobile / HideOnMobile - Conditional rendering helpers
 */
export const ShowOnMobile = ({ children }) => {
  const { isMobile } = useMobile();
  return isMobile ? children : null;
};

export const HideOnMobile = ({ children }) => {
  const { isMobile } = useMobile();
  return !isMobile ? children : null;
};

export const ShowOnDesktop = ({ children }) => {
  const { isDesktop } = useMobile();
  return isDesktop ? children : null;
};

export const HideOnDesktop = ({ children }) => {
  const { isDesktop } = useMobile();
  return !isDesktop ? children : null;
};